import Vue from 'vue'
import Router from 'vue-router'
import store from './store'

Vue.use(Router)

const router = new Router({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/landing',
      name: 'Landing',
      component: () => import('@/views/LandingPage'),
      meta: { requiresAuth: false },
    },
    {
      path: '/consultoria',
      name: 'Consultoria',
      component: () => import('@/views/ConsultoriaPage'),
      meta: { requiresAuth: false },
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/Login'),
      meta: { requiresAuth: false },
    },
    // Dashboard Tenant Admin (sem sidebar)
    {
      path: '/tenant-admin',
      name: 'DashboardTenantAdmin',
      component: () => import('@/views/dashboard/DashboardTenantAdmin'),
      meta: { 
        requiresAuth: true,
        allowedRoles: ['TENANT_ADMIN'],
      },
    },
    // Configurações do Admin (sem sidebar)
    {
      path: '/admin-settings',
      name: 'AdminSettings',
      component: () => import('@/views/dashboard/settings/TenantTest'),
      meta: { 
        requiresAuth: true,
        allowedRoles: ['TENANT_ADMIN'],
      },
    },
    // Jira OAuth Success (callback após autorização)
    {
      path: '/integrations/jira/success',
      name: 'JiraSuccess',
      component: () => import('@/views/integrations/JiraSuccess'),
      meta: { requiresAuth: false }, // OAuth callback não requer auth
    },
    // Jira OAuth Error (erros durante OAuth)
    {
      path: '/integrations/jira/error',
      name: 'JiraError',
      component: () => import('@/views/integrations/JiraError'),
      meta: { requiresAuth: false },
    },
    // Microsoft OAuth Success (callback após autorização)
    {
      path: '/integrations/microsoft/success',
      name: 'MicrosoftSuccess',
      component: () => import('@/views/integrations/MicrosoftSuccess'),
      meta: { requiresAuth: false }, // OAuth callback não requer auth
    },
    // Microsoft OAuth — handle backend redirect with query param
    {
      path: '/admin/integrations',
      name: 'MicrosoftCallbackRedirect',
      component: () => import('@/views/integrations/MicrosoftSuccess'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: () => import('@/views/dashboard/Index'),
      meta: { requiresAuth: true },
      children: [
        // Dashboard
        {
          name: 'Dashboard',
          path: '',
          component: () => import('@/views/dashboard/Dashboard'),
        },
        {
          name: 'Dashboard V2',
          path: 'dashboard-v2', 
          component: () => import('@/views/dashboard/DashboardManagerV2'),
          meta: { 
            requiresAuth: true,
            // Restrito para gestores (e admins que assumem a visão de gestor)
            allowedRoles: ['MANAGER', 'TENANT_ADMIN'],
          },
        },
        // Theme Demo
        {
          name: 'Theme Demo',
          path: 'theme-demo',
          component: () => import('@/views/ThemeDemo'),
        },
        // Campanhas
        {
          name: 'Campanhas',
          path: 'campaigns',
          component: () => import('@/views/dashboard/Campaigns'),
        },
        // Relatórios
        {
          name: 'Relatórios',
          path: 'reports',
          component: () => import('@/views/dashboard/Reports'),
        },
        // Configurações
        {
          name: 'Configurações',
          path: 'settings',
          component: () => import('@/views/dashboard/settings/Index'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['PLATFORM_OWNER', 'TENANT_ADMIN'],
          },
        },
        {
          name: 'Conta SaaS',
          path: 'settings/saas-account',
          component: () => import('@/views/dashboard/settings/SaasAccount'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['PLATFORM_OWNER', 'TENANT_ADMIN'],
          },
        },
        {
          name: 'Tenant',
          path: 'settings/tenant',
          component: () => import('@/views/dashboard/settings/Tenant'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['PLATFORM_OWNER', 'TENANT_ADMIN'],
          },
        },
        {
          name: 'Platform Owner',
          path: 'settings/platform-owner',
          component: () => import('@/views/dashboard/settings/PlatformOwner'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['PLATFORM_OWNER'],
          },
        },
        {
          name: 'Partner Admin',
          path: 'settings/partner-admin',
          component: () => import('@/views/dashboard/settings/PartnerAdmin'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['PLATFORM_OWNER'],
          },
        },
        {
          name: 'Configurações de Gestor',
          path: 'settings/manager',
          component: () => import('@/views/dashboard/settings/Manager'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['MANAGER', 'TENANT_ADMIN', 'PLATFORM_OWNER'],
          },
        },
        {
          name: 'Minhas Configurações',
          path: 'settings/user',
          component: () => import('@/views/dashboard/settings/User'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['USER', 'MANAGER', 'TENANT_ADMIN', 'PLATFORM_OWNER'],
          },
        },
        // Usuários
        {
          name: 'Usuários',
          path: 'users',
          component: () => import('@/views/dashboard/Users'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['MANAGER', 'TENANT_ADMIN', 'PLATFORM_OWNER'],
          },
        },
        // Departamentos
        {
          name: 'Departamentos',
          path: 'departments',
          component: () => import('@/views/dashboard/Departments'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['MANAGER', 'TENANT_ADMIN', 'PLATFORM_OWNER'],
          },
        },
        // Times
        {
          name: 'Times',
          path: 'teams',
          component: () => import('@/views/dashboard/Teams'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['MANAGER', 'TENANT_ADMIN', 'PLATFORM_OWNER'],
          },
        },
        // PDI
        {
          name: 'PDI',
          path: 'pdis',
          component: () => import('@/views/dashboard/PDI'),
          meta: { 
            requiresAuth: true,
          },
        },
        // PDI Manager (apenas para MANAGER)
        {
          name: 'PDI Manager',
          path: 'pdi-manager',
          component: () => import('@/views/dashboard/PdiManager'),
          meta: {
            requiresAuth: true,
            allowedRoles: ['MANAGER', 'TENANT_ADMIN'],
          },
        },
        // Aprovações Gerais (PDI e One-on-One)
        {
          name: 'Aprovações',
          path: 'approval',
          component: () => import('@/views/dashboard/Approval'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['MANAGER', 'TENANT_ADMIN'],
          },
        },
        // One-on-One
        {
          name: 'One-on-One',
          path: 'one-on-ones',
          component: () => import('@/views/dashboard/OneOnOne'),
          meta: { 
            requiresAuth: true,
          },
        },
        // Skills (Competências)
        {
          name: 'Competências',
          path: 'skills',
          component: () => import('@/views/dashboard/Skills'),
          meta: { 
            requiresAuth: true,
          },
        },
        // Feedbacks
        {
          name: 'Feedbacks',
          path: 'feedbacks',
          component: () => import('@/views/dashboard/Feedbacks'),
          meta: { 
            requiresAuth: true,
          },
        },
        // Desenvolvimento (Em breve)
        {
          name: 'Desenvolvimento',
          path: 'desenvolvimento',
          component: () => import('@/views/dashboard/Desenvolvimento'),
          meta: { requiresAuth: true },
        },
        // Engajamento (Em breve)
        {
          name: 'Engajamento',
          path: 'engajamento',
          component: () => import('@/views/dashboard/Engajamento'),
          meta: { requiresAuth: true },
        },
        // Recrutamento (Em breve)
        {
          name: 'Recrutamento',
          path: 'recrutamento',
          component: () => import('@/views/dashboard/Recrutamento'),
          meta: { requiresAuth: true },
        },
        // Pages
        {
          name: 'User Profile',
          path: 'pages/user',
          component: () => import('@/views/dashboard/pages/UserProfile'),
        },
        {
          name: 'Notifications',
          path: 'notifications',
          component: () => import('@/views/dashboard/component/Notifications'),
        },
        {
          name: 'Icons',
          path: 'components/icons',
          component: () => import('@/views/dashboard/component/Icons'),
        },
        {
          name: 'Typography',
          path: 'components/typography',
          component: () => import('@/views/dashboard/component/Typography'),
        },
        // Tables
        {
          name: 'Regular Tables',
          path: 'tables/regular-tables',
          component: () => import('@/views/dashboard/tables/RegularTables'),
        },
        // Maps
        {
          name: 'Google Maps',
          path: 'maps/google-maps',
          component: () => import('@/views/dashboard/maps/GoogleMaps'),
        },
        // Upgrade
        {
          name: 'Upgrade',
          path: 'upgrade',
          component: () => import('@/views/dashboard/Upgrade'),
        },
        // Test API
        {
          name: 'Test API',
          path: 'test-api',
          component: () => import('@/views/dashboard/TestAPI'),
        },
        // Tenant Details (Admin)
        {
          name: 'TenantDetails',
          path: 'tenants/:id',
          component: () => import('@/views/dashboard/admin/TenantDetails'),
        },
        // Dashboard Manager (modo visualização com sidebar)
        {
          name: 'ManagerViewOnly',
          path: 'manager/:managerId',
          component: () => import('@/views/dashboard/DashboardManager'),
          meta: { 
            requiresAuth: true,
            allowedRoles: ['TENANT_ADMIN', 'MANAGER'],
          },
        },
      ],
    },
  ],
})

// Navigation Guard - Verificar autenticação e permissões
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('firebaseToken')
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  console.log('Navigation Guard:', {
    to: to.path,
    from: from.path,
    requiresAuth,
    hasToken: !!token,
  })

  // Carregar configurações do usuário se autenticado e ainda não carregado
  if (token && !store.state.userConfig) {
    try {
      // AGUARDAR o carregamento das configs para aplicar tema antes da navegação
      await store.dispatch('loadUserConfig')
    } catch (error) {
      console.warn('Falha ao carregar configurações do usuário:', error)
      // Não bloquear navegação por falha no carregamento de configs
    }
  }

  // Visitante não autenticado acessando raiz → redirecionar para landing
  if (to.path === '/' && !token) {
    next('/landing')
    return
  }

  if (requiresAuth && !token) {
    // Rota protegida sem autenticação - redirecionar para login
    console.warn('Sem token, redirecionando para login')
    next('/login')
    return
  }

  if (to.path === '/login' && token) {
    // Já está autenticado tentando acessar login - redirecionar baseado no role
    try {
      const userStr = localStorage.getItem('user')
      const user = userStr ? JSON.parse(userStr) : null
      
      if (user && user.role === 'TENANT_ADMIN') {
        next('/tenant-admin')
        return
      }
      if (user && user.role === 'MANAGER') {
        next('/')
        return
      }
      if (user && user.role === 'PLATFORM_OWNER') {
        next('/')
        return
      }
    } catch (error) {
      console.error('Erro ao verificar role:', error)
    }
    
    next('/')
    return
  }

  // TENANT_ADMIN não pode acessar rotas com sidebar (apenas /tenant-admin e /manager/:id)
  // EXCETO quando está em modo visualização de gestor
  if (to.path === '/' || to.path.startsWith('/dashboard')) {
    try {
      const userStr = localStorage.getItem('user')
      const user = userStr ? JSON.parse(userStr) : null
      
      if (user && user.role === 'TENANT_ADMIN') {
        const isViewOnly = to.query.viewOnly === 'true' || from.query.viewOnly === 'true' || store.getters.isViewOnlyMode
        if (!isViewOnly) {
          // Redirecionar TENANT_ADMIN para sua tela própria
          next('/tenant-admin')
          return
        }
        // Em modo viewOnly, permitir navegar nas telas do gestor
        console.log('TENANT_ADMIN em modo visualização, permitindo acesso a:', to.path)
      }
    } catch (error) {
      console.error('Erro ao verificar role:', error)
    }
  }

  // ===== BLOQUEAR PERFIL DO GESTOR EM MODO VISUALIZAÇÃO =====
  // TENANT_ADMIN em modo viewOnly NÃO pode acessar /pages/user (perfil do gestor)
  if (to.path === '/pages/user') {
    const isViewOnly = to.query.viewOnly === 'true' || from.query.viewOnly === 'true' || store.getters.isViewOnlyMode
    if (isViewOnly) {
      console.warn('🚫 Acesso ao perfil bloqueado em modo visualização')
      store.commit('setSnackbar', {
        show: true,
        color: 'warning',
        text: 'Modo visualização — não é possível acessar o perfil do gestor'
      })
      next(false) // Bloquear navegação
      return
    }
  }

  // Verificar permissões por role
  const allowedRoles = to.meta.allowedRoles
  if (allowedRoles && allowedRoles.length > 0) {
    try {
      const userStr = localStorage.getItem('user')
      const user = userStr ? JSON.parse(userStr) : null

      console.log('Verificando permissões:', {
        allowedRoles,
        userRole: user?.role,
        path: to.path,
      })

      if (!user || !user.role) {
        console.warn('Usuário sem role definida, acesso negado')
        next('/')
        return
      }

      // Verificar se o role do usuário está na lista de permitidos
      if (!allowedRoles.includes(user.role)) {
        console.warn(`Acesso negado: ${user.role} não tem permissão para acessar ${to.path}`)
        // Redirecionar para dashboard com mensagem
        next({
          path: '/',
          query: { 
            denied: '1',
            message: 'Você não tem permissão para acessar esta página',
          },
        })
        return
      }
      
      console.log('Permissão concedida, prosseguindo para:', to.path)
    } catch (error) {
      console.error('Erro ao verificar permissões:', error)
      next('/')
      return
    }
  }

  // ===== MODO VISUALIZAÇÃO: Preservar query params e atualizar store =====
  const isCurrentlyViewOnly = from.query.viewOnly === 'true'
  const isGoingToViewOnly = to.query.viewOnly === 'true'
  
  // Se está em modo visualização e navegando sem os query params, preservá-los
  if (isCurrentlyViewOnly && !isGoingToViewOnly && to.path !== '/tenant-admin' && to.path !== '/login') {
    // Preservar TODOS os query params do modo visualização, incluindo managerId
    const managerId = from.query.managerId || from.params.managerId || store.getters.viewOnlyManagerId
    next({
      path: to.path,
      query: {
        ...to.query,
        viewOnly: 'true',
        managerId: managerId,
        managerName: from.query.managerName,
        managerRole: from.query.managerRole
      },
      params: to.params
    })
    return
  }
  
  // Atualizar Vuex store com estado do modo visualização
  if (to.query.viewOnly === 'true') {
    // Buscar managerId de todas as fontes possíveis (params, query, store)
    const managerId = to.params.managerId || from.params.managerId || to.query.managerId || from.query.managerId || store.getters.viewOnlyManagerId
    store.dispatch('setViewOnlyMode', {
      active: true,
      managerId: managerId,
      managerName: to.query.managerName || from.query.managerName || store.getters.viewOnlyManagerName,
      managerRole: to.query.managerRole || from.query.managerRole || store.getters.viewOnlyManagerRole
    })
  } else if (to.path === '/tenant-admin' || to.path === '/login') {
    // Limpar modo visualização ao voltar para tenant-admin ou login
    store.dispatch('clearViewOnlyMode')
  }

  // Permitir navegação
  next()
})

export default router
